import { apiInstance, currentEnvAppConfig, useAuth } from '@multiplatform-apps/service-trips';
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { SessionWarningModal } from '../components/session-warning-modal/session-warning-modal';
import { useAbsoluteTimeout } from '../hooks/use-absolute-timeout';
import { useIdleTimeout } from '../hooks/use-idle-timeout';
import { EXTENSION_COUNT_KEY, MAX_EXTENSIONS, SessionTimeoutContextType, WARNING_DURATION_MS } from './types';

const SessionTimeoutContext = createContext<SessionTimeoutContextType>({
  resetIdleTimer: () => undefined,
});

const { idleTimeoutMs: configIdleTimeoutMs, absoluteTimeoutMs: configAbsoluteTimeoutMs } = currentEnvAppConfig;

declare global {
  interface Window {
    __SESSION_IDLE_TIMEOUT_MS?: number;
    __SESSION_ABSOLUTE_TIMEOUT_MS?: number;
  }
}

// Dev override: paste in console to shorten timeouts for testing
// window.__SESSION_IDLE_TIMEOUT_MS = 60000;   // 1 min idle
// window.__SESSION_ABSOLUTE_TIMEOUT_MS = 120000; // 2 min absolute
const getIdleTimeoutMs = (): number =>
  window.__SESSION_IDLE_TIMEOUT_MS ?? configIdleTimeoutMs;
const getAbsoluteTimeoutMs = (): number =>
  window.__SESSION_ABSOLUTE_TIMEOUT_MS ?? configAbsoluteTimeoutMs;

export const SessionTimeoutProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { logout, isAuthenticated } = useAuth();

  const [warningOpen, setWarningOpen] = useState(false);
  const [extensionCount, setExtensionCount] = useState(() => {
    const stored = sessionStorage.getItem(EXTENSION_COUNT_KEY);
    return stored ? Number(stored) : 0;
  });

  const warningOpenRef = useRef(warningOpen);
  warningOpenRef.current = warningOpen;

  const forceLogoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutTriggeredRef = useRef(false);

  const forceLogout = useCallback(() => {
    timeoutTriggeredRef.current = true;
    if (forceLogoutTimerRef.current) {
      clearTimeout(forceLogoutTimerRef.current);
      forceLogoutTimerRef.current = null;
    }
    setWarningOpen(false);
    localStorage.setItem('session_expired_at', Date.now().toString());
    logout();
  }, [logout]);

  const calculateEffectiveWarningMs = useCallback(
    (timeoutMs: number): number => Math.min(WARNING_DURATION_MS, timeoutMs - 10_000),
    []
  );

  const effectiveIdleWarningMs = calculateEffectiveWarningMs(getIdleTimeoutMs());
  const effectiveAbsoluteWarningMs = calculateEffectiveWarningMs(getAbsoluteTimeoutMs());

  const startWarning = useCallback((warningDurationMs: number) => {
    if (timeoutTriggeredRef.current) return;
    setWarningOpen(true);

    // Schedule forced logout after warning period expires
    if (forceLogoutTimerRef.current) clearTimeout(forceLogoutTimerRef.current);
    forceLogoutTimerRef.current = setTimeout(() => {
      forceLogout();
    }, warningDurationMs);
  }, [forceLogout]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (forceLogoutTimerRef.current) {
        clearTimeout(forceLogoutTimerRef.current);
      }
    };
  }, []);

  // Factory for creating timeout handlers (warning + timeout pair)
  const createTimeoutHandlers = useCallback(
    (warningDurationMs: number) => ({
      onWarning: () => startWarning(warningDurationMs),
      onTimeout: () => {
        if (!warningOpenRef.current) forceLogout();
      },
    }),
    [startWarning, forceLogout]
  );

  const idleHandlers = createTimeoutHandlers(effectiveIdleWarningMs);
  const absoluteHandlers = createTimeoutHandlers(effectiveAbsoluteWarningMs);

  const { resetIdleTimer } = useIdleTimeout({
    timeoutMs: getIdleTimeoutMs(),
    warningMs: effectiveIdleWarningMs,
    ...idleHandlers,
    enabled: isAuthenticated,
  });

  const { resetAbsoluteWarning } = useAbsoluteTimeout({
    timeoutMs: getAbsoluteTimeoutMs(),
    warningMs: effectiveAbsoluteWarningMs,
    ...absoluteHandlers,
    enabled: isAuthenticated,
  });

  // Register transient failure warning observer
  useEffect(() => {
    if (!isAuthenticated) return;
    apiInstance.setTransientFailureWarningObserver(() => {
      startWarning(effectiveIdleWarningMs);
    });
    return () => {
      apiInstance.setTransientFailureWarningObserver(() => undefined);
    };
  }, [isAuthenticated, startWarning, effectiveIdleWarningMs]);

  const handleExtend = useCallback(() => {
    const newCount = extensionCount + 1;
    setExtensionCount(newCount);
    sessionStorage.setItem(EXTENSION_COUNT_KEY, String(newCount));
    setWarningOpen(false);
    if (forceLogoutTimerRef.current) {
      clearTimeout(forceLogoutTimerRef.current);
      forceLogoutTimerRef.current = null;
    }
    resetIdleTimer();
    resetAbsoluteWarning();
    timeoutTriggeredRef.current = false;
  }, [resetIdleTimer, resetAbsoluteWarning, extensionCount]);

  const extensionsRemaining = MAX_EXTENSIONS - extensionCount;

  const contextValue = useMemo(
    () => ({ resetIdleTimer }),
    [resetIdleTimer],
  );

  return (
    <SessionTimeoutContext.Provider value={contextValue}>
      {children}
      <SessionWarningModal
        isOpen={warningOpen}
        onExtend={handleExtend}
        onLogout={forceLogout}
        extensionsRemaining={extensionsRemaining}
      />
    </SessionTimeoutContext.Provider>
  );
};

export const useSessionTimeout = (): SessionTimeoutContextType =>
  useContext(SessionTimeoutContext);
